import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useCopy } from '@/hooks/useCopy';
import {
  pendingRenewalLabel,
  renewalCtaLabelKey,
  renewalYearMsgFromLabel,
  type RenewalCtaState,
  type RenewalYearLabel,
} from '@/lib/renewal-display';
import { useMsg } from '@/i18n/msg';

/**
 * The Signature Grant renewal card (F-027; copy + year rulings from the 08-19
 * call live in lib/renewal-display.ts), today the copy editor's preview of the
 * renewal wording rather than a family surface.
 *
 * How a family meets the renewal now: the wizard's submit writes the server's
 * renewal into RENEWAL_MINE_QUERY_KEY (#2332) and lands on /bills with no
 * success toast. BillsPage's one-line renewal action (the compressed form of
 * this card) then reads that cache and shows the status sentence through
 * `RenewalStatusText` below (#2331), so that line IS the confirmation. No
 * family page mounts the card; `RenewalCardView` is mounted by the copy
 * editor's preview pane (CopyPreviewPane, D-CS-12) with a stand-in state. The
 * self-fetching `RenewalCard` wrapper had no importer and was deleted (#2353).
 *
 * Because the preview and the /bills line share `RenewalStatusText`, and the
 * words resolve through copy() (#1585 / B-573) with the seeds in
 * lib/copySlots.ts, the preview cannot promise a sentence the family page
 * does not show.
 *
 * The states, as the card renders them (the /bills line follows the same
 * derivation, deriveRenewalCta):
 *  - no renewal (or terminal) → "Start your renewal"
 *  - draft / needs_more_info  → "Continue/Finish your renewal" (editable)
 *  - submitted / under_review → a status line, no button (the wizard would just
 *    redirect for a non-editable renewal — a dead-end we avoid here)
 *  - declined                 → not approved, ask the case manager; no button
 *                               (DECISIONS 2026-10-11, superseding #1490's neutral line)
 *  - waitlisted (notice sent) → on the waitlist, and what happens next; no button
 *  - handed_off               → nothing; the grant's own status speaks (until
 *                               staff Decide that grant, its /bills row says
 *                               the renewal was approved and the grant is
 *                               being set up — #2745, GrantRow)
 *  - fetch failed (non-404)   → say we couldn't load it; invite nothing (a
 *    failed request is not evidence the family has no renewal)
 * Once a renewal record exists its year (intake.application_type) is named on
 * the card.
 */
/**
 * The status sentence a family reads once its renewal is in (#2331): the
 * pending line (submitted / under review), the declined line, or the
 * waitlisted line (DECISIONS 2026-10-11).
 *
 * ONE renderer for both family surfaces — the card below (the copy editor's
 * preview target, D-CS-12) and the `/bills` renewal line — so the preview
 * cannot promise a sentence the family page does not show. The words are
 * TRF's `apply.renewal.*` copy slots; the punctuation around them is the
 * grants catalog's, so zh can use full-width marks (#2109 R3-B3).
 *
 * `yearText` is the year already in the reader's language ("Year 2"), or
 * null when the renewal names no year.
 */
export function RenewalStatusText({
  state,
  yearText,
  className,
}: {
  state: Extract<RenewalCtaState, { kind: 'pending' | 'declined' | 'waitlisted' }>;
  yearText: string | null;
  className?: string;
}) {
  const { copy } = useCopy();
  const { t } = useTranslation('grants');
  if (state.kind === 'declined' || state.kind === 'waitlisted') {
    // DECISIONS 2026-10-16: the platform does not communicate a decline or a
    // waitlist. Once TRF's email is recorded, the card says only that TRF
    // reviewed the renewal and emailed the family. No outcome, no date, no button.
    return (
      <p className={className} data-testid={`renewal-${state.kind}-line`}>
        {t('renewal.reviewedLine')}
      </p>
    );
  }
  return (
    <p className={className} data-testid="renewal-pending-line">
      {/* The year is data; the slot places it with {{year}}, so another
          language can put it where its grammar wants it (lane L9). */}
      <Trans
        t={t}
        i18nKey="renewal.pendingLine"
        values={{
          title: yearText
            ? copy('apply.renewal.request_title_with_year', { year: yearText })
            : copy('apply.renewal.request_title'),
          status: pendingRenewalLabel(state.status, copy),
        }}
        components={{ status: <span className="font-medium text-stevens-indigo" /> }}
      />
    </p>
  );
}

/**
 * The card's renderer, split from the fetch so the copy editor's preview pane
 * can show it with a stand-in state (D-CS-12 — every editable string has
 * somewhere to be seen). Families read the /bills renewal line instead; the
 * preview stays honest because both render the status through
 * `RenewalStatusText`.
 */
export function RenewalCardView({
  state,
  year,
}: { state: RenewalCtaState; year: RenewalYearLabel | null }) {
  const navigate = useNavigate();
  const { copy, ready: copyReady } = useCopy();
  const { t } = useTranslation('grants');
  const resolve = useMsg();
  // "Year 2" is one key with the number (#2109 R3-B3); the English label the
  // state carries is only the identifier.
  const yearText = resolve(renewalYearMsgFromLabel(year)) || null;

  // Guardrail 7: hold paint until the copy read settles, so a family never
  // sees the seed replaced by TRF's wording mid-read. The fetch hold lives in
  // `RenewalCard`; first paint is fully resolved either way.
  if (!copyReady) return null;
  // #1490 (Victor, 2026-10-05): an approved renewal with its grant request is a
  // silent hand-off — the grant's own status, on the grant surfaces, speaks.
  if (state.kind === 'handed_off') return null;

  return (
    <div className="rounded-lg border border-stevens-indigo/30 bg-stevens-indigo/5 p-5 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
      {/* Below `sm` the button stacks under the text and its label may wrap,
          so a long translated label never squeezes the copy or overflows. */}
      <div className="min-w-0 sm:flex-1">
        <h3 className="font-serif text-base font-medium text-foreground">
          {copy('apply.renewal.card_heading')}
          {/* The space is layout, not copy (see BillsPage's renewal row). */}
          {yearText && <span className="text-stevens-indigo">{' '}{t('renewal.yearSuffix', { year: yearText })}</span>}
        </h3>
        {state.kind === 'pending' || state.kind === 'declined' || state.kind === 'waitlisted' ? (
          <RenewalStatusText state={state} yearText={yearText} className="text-body text-stevens-ink-3 mt-0.5" />
        ) : state.kind === 'unknown' || state.kind === 'needs_info' ? (
          <p className="text-body text-stevens-ink-3 mt-0.5">
            {/* #2527: the same line /bills shows when TRF sent the renewal back. */}
            {state.kind === 'unknown' ? copy('apply.renewal.error_line') : t('renewal.needsInfo.line')}
          </p>
        ) : (
          <p className="text-body text-stevens-ink-3 mt-0.5">
            {copy('apply.renewal.invite_line')}
          </p>
        )}
      </div>
      {(state.kind === 'none' || state.kind === 'editable' || state.kind === 'needs_info') && (
        <Button
          onClick={() => navigate('/renew')}
          variant="brand"
          className="h-auto min-h-10 max-w-full whitespace-normal py-2 text-start sm:shrink-0"
        >
          {resolve({ key: renewalCtaLabelKey(state) })}
        </Button>
      )}
    </div>
  );
}
